import * as React from 'react';
import { CreateTask } from './CreateTask';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import Dialog from '@fluentui/react/lib/Dialog';
import { SearchBox } from '@fluentui/react/lib/SearchBox';
import { IAbo } from '../../api/model/IAbo';
import * as ss from 'string-similarity';
import { IProject } from '../../api/model/IProject';
import { Project } from '../../api/Project';
import { Configuration } from '../../api/Configuration';
import { AbsenceList } from './AbsenceList';
import { AllTasksList } from './AllTasksList';
import { DatePicker } from '@fluentui/react/lib/DatePicker';
import { startOfMonth, endOfMonth } from '../../utils/time.utils'
import { useGlobalState } from '../../service/globalState.service';
import { FluentTheme } from "@fluentui/react/lib/Theme";
import { DarkTheme } from './App';
const ScrollList: React.FC<{ offset: number }> = ({ offset, children }) => (
  <>
    <div style={{ height: `calc(100vh - ${offset}px)`, overflow: 'auto' }}>
      {children}
    </div>
  </>
)

export const Tabs: React.FC = () => {
  const [hidden, setHidden] = React.useState(true);
  const [filter, setFilter] = React.useState<string>();
  const [itemStartDate, setDate] = React.useState<Date>();
  const [filterStart, setFilterStart] = React.useState<Date>();
  const [filterEnd, setFilterEnd] = React.useState<Date>();
  const [projects, setProjects] = React.useState<IProject[]>([]);
  const [{ darkTheme, showAllTasks }] = useGlobalState('settings');
  const [ message ] = useGlobalState('message');
  const theme = darkTheme ? DarkTheme : FluentTheme;
    const background = darkTheme ? theme.semanticColors.bodyBackground : 'white';
  const [absenceProjectFilter, setAbsenceProjectFilter] = React.useState('');

  React.useEffect(() => {
    fetchConfiguration();
    fetchProjects();
    parseStart();

    if (!absenceProjectFilter) {
      parseSubject();
    }
  }, [])

  const fetchConfiguration = async () => {
    const configEntries = await Configuration.getAll();
    const absenceTaskSendOnly = configEntries.find(c => c.Id === 'AbsenceTaskSendOnly');
    if (absenceTaskSendOnly?.Value !== 'True') {
      return;
    }

    const absenceProjectFilter = configEntries.find(c => c.Id === 'AbsenceFilterProjNameLike');
    if (absenceProjectFilter?.Value) {
      setAbsenceProjectFilter(absenceProjectFilter.Value);
      setFilter('');
    }
  }

  const fetchProjects = async () => {
    const projects = await Project.getAll();
    setProjects(projects);
  }

  const parseSubject = () => {
    const { item } = Office.context.mailbox;
    item.subject.getAsync(r => {
      if (r.value) setFilter(r.value);
      else setFilter('');
    })
  }

  const parseStart = async (): Promise<Date> => {
    const { item } = Office.context.mailbox;
    return new Promise((resolve) => {
      item.start.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          setDate(result.value);
        } else {
          setDate(new Date());
        }
      });
    });
  };
  
  const commonSearch = () => (<>
    <SearchBox
      value={filter || ''}
      onChange={(e, v) => setFilter(v)}
      placeholder='Search tasks'
    />
  </>);

  const dateRangePicker = () => (
    <>
    <div style={{ display: 'flex', gap: 16, marginBottom: 16, marginTop: 8 }}>
          <DatePicker
            label="from"
            value={filterStart}
            onSelectDate={setFilterStart}
            placeholder={ showAllTasks ? "Select from Date" : startOfMonth(itemStartDate).toDateString()}
            initialPickerDate={startOfMonth(itemStartDate)}
            styles={{
              textField: {
                selectors: {
                  '.ms-TextField-fieldGroup': {
                    paddingRight: 8,
                  }
                },
              },
            }}
          />
          <DatePicker
            label="to"
            value={filterEnd}
            onSelectDate={setFilterEnd}
            placeholder={ showAllTasks ? "Select to Date" : endOfMonth(itemStartDate).toDateString()}
            initialPickerDate={endOfMonth(itemStartDate)}
            styles={{
              textField: {
                selectors: {
                  '.ms-TextField-fieldGroup': {
                    paddingRight: 8,
                  }
                },
              },
            }}
          />
        </div>

  </>);

  const aboFilter = (abos: IAbo[]): IAbo[] => {
    if (!abos || abos.length === 0 || !filter) return abos;
  
    const lowerCaseFilter = filter.toLowerCase();
  
    const projectNames = abos.map(a => a.ProjectName.toLowerCase());
    const taskNames = abos.map(a => a.TaskName.toLowerCase());
  
    const { ratings: projectRatings } = ss.findBestMatch(lowerCaseFilter, projectNames);
    const { ratings: taskRatings } = ss.findBestMatch(lowerCaseFilter, taskNames);
  
    let ratedAbos;
    let ratingThreshold = 0.25;

    ratedAbos = abos.map((a, i) => {
      const projectName = projectNames[i];
      const taskName = taskNames[i];

      const projectBonus = projectName.includes(lowerCaseFilter) ? 0.5 : 0;
      const taskBonus = taskName.includes(lowerCaseFilter) ? 0.5 : 0;

      const combinedRating = projectRatings[i].rating + projectBonus + taskRatings[i].rating + taskBonus;

      return { abo: a, combinedRating };
    }).filter(({ combinedRating }) => combinedRating > ratingThreshold);
    
    ratedAbos.sort((a, b) => b.combinedRating - a.combinedRating);
    return ratedAbos.map(({ abo }) => abo);
  }

  const dismiss = () => setHidden(true);
  return (
    <>
      {absenceProjectFilter ? (
        <>
          {commonSearch()}
          <ScrollList offset={ message == null ? 245 : 295 }>
            <AbsenceList filter={aboFilter} absenceProjectFilter={absenceProjectFilter} />
          </ScrollList>
        </>
      ) : (
        <>
        {commonSearch()}
        {itemStartDate && dateRangePicker()}
        <ScrollList offset={ message == null ? 245 : 295 }>
          <AllTasksList 
            filter={aboFilter}
            filterStart={filterStart}
            filterEnd={filterEnd}
          />
        </ScrollList>
        <div
          style={{
            position: 'fixed',
            background: background,
            bottom: 0,
            left: 0,
            right: 0,
            height: 60,
            borderTop: '1.5px solid lightgray',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
          <div style={{ paddingRight: 20 }}>
            <PrimaryButton
              text='Create Task'
              onClick={() => setHidden(false)}
            />
          </div>
        </div>
      </>
      )}
      <Dialog
        hidden={hidden}
        dialogContentProps={{
          title: 'Create new task',
          closeButtonAriaLabel: 'Close',
          showCloseButton: true
        }}
        onDismiss={dismiss}
      >
        <CreateTask onCreateSuccess={dismiss} projects={projects} />
      </Dialog>
    </>
  )
}